import mqtt from "mqtt";
import * as mqttTopics from "@common/constants/mqttTopics";
import * as mqttQos from "@common/constants/mqttQos";
import NOTIFICATION_TYPES from "@common/constants/notificationTypes";
import notification_pb from "../util/protobuf/notification_pb";
import log_pb from "../util/protobuf/log_pb";
import { BehaviorSubject, Subject } from "rxjs";
import { filter } from "rxjs/operators";

// MQTT 3.1.1 SUBACK return code for "failure".
const REFUSED_QOS = 128;
const REFUSED_BACKOFF_BASE_MS = 30 * 1000;
const REFUSED_BACKOFF_MAX_MS = 10 * 60 * 1000;

/**
 * Service containing all businesslogic to communicate with the MQTT Broker
 * And exposes rxjs streams to the components
 */
class MqttService {
  constructor() {
    this.channel = null;
    this.subscriptions = [];
    this.connected = false;
    this.connected$ = new BehaviorSubject();
    this.notification$ = new Subject();
    this.frequentLog$ = new Subject();
    this.frequentLogResponse$ = new BehaviorSubject();
    this.forceReconnect = false;
    // Topics the broker refused (SUBACK qos 0x80, or the connection was
    // killed right after we sent SUBSCRIBE — RabbitMQ 3.6 does the latter).
    // topic -> { count, retryAfter }. Kept out of `subscriptions` so a
    // reconnect never replays them until their backoff has expired.
    this.refusedTopics = new Map();
    // Topics we have sent SUBSCRIBE for but not yet received a SUBACK.
    this.pendingSubscribes = new Set();

    this.clientID = null;
    this.username = null;
    this.password = null;
  }

  get connectedMQTT() {
    return this.channel ? this.channel.connected : false;
  }
  /**
   * Exposes notifications stream filtered by machine uuid
   * @param {string} machineUUID
   */
  notificationsByMachine$(machineUUID) {
    return this.notification$.pipe(
      filter((notification) => notification.device == machineUUID),
    );
  }

  /**
   * Exposes stream that emits values when a notification is of type
   * COMMAND_EXECUTED
   */
  notificationsExecuted() {
    return this.notification$.pipe(
      filter(
        (notification) =>
          notification.type === NOTIFICATION_TYPES.NOT_COMMAND_EXECUTED,
      ),
    );
  }

  /**
   * Exposes stream that emits values when a notification is of type
   * COMMAND_EXECUTED and the provided sequence number matches the
   * notifications sequence number
   * @param {Number} sequence
   */
  notificationsExecutedWithSequence(sequence) {
    return this.notification$.pipe(
      filter(
        (notification) =>
          notification.type === NOTIFICATION_TYPES.NOT_COMMAND_EXECUTED,
      ),
      filter((notification) => notification.commandSeq === sequence),
    );
  }

  /**
   * Exposes frequent log stream filtered by machine uuid
   * TODO: NOT WORKING ATM
   * @param {string} machineUUID
   */
  frequentLogByMachine$(machineUUID) {
    return this.frequentLog$.pipe(
      filter((frequentlog) => frequentlog.processSummaryDevice == machineUUID),
    );
  }

  /**
   * Connect to the mqtt broker
   * @param {string} clientID a v4 UUID
   * @param {string} username
   * @param {string} password
   */
  connect(clientID, username, password) {
    // Idempotent: one mqtt.js client per page. PortalLayout calls connect()
    // on every new `userInfo.user` object, and /me + /subscription-status/
    // each produce one; a second connect() while the first CONNACK was still
    // in flight created a second client with the SAME clientId, and the two
    // kicked each other off the broker every reconnectPeriod for the life of
    // the tab (Monitor flapping "Connection lost, reconnecting…", 2026-08-29).
    // mqtt.js reconnects an existing client by itself; reconnect()/end()
    // release the client explicitly before a deliberate new connect.
    if (this.channel) {
      console.dev("MQTT: connect ignored, a client already exists");
      return;
    }
    this.clientID = clientID;
    this.username = username;
    this.password = password;

    console.dev(`connecting with:`);
    console.dev(`client id: ${this.clientID}`);
    console.dev(`username: ${this.username}`);
    console.dev(`password: ${this.password}`);
    const options = {
      username: this.username,
      password: this.password,
      clientId: this.clientID,
      reconnectPeriod: 5000,
      connectTimeout: 30 * 5000,
      // We replay subscriptions ourselves in _handleConnect so a refused
      // topic can be held back with backoff instead of re-sent on every
      // reconnect (which is what turned one refused keg topic into a
      // ~20 reconnects/s storm against the broker).
      resubscribe: false,
      will: {
        topic: mqttTopics.lastWillTopic + this.clientID,
        payload: "offline",
        qos: mqttQos.atMostOnce,
        retain: false,
      },
    };

    this.channel = mqtt.connect("wss://broker.minibrew.io:15675/ws", options);
    this.channel.on("connect", () => this._handleConnect());
    this.channel.on("close", () => this._handleClose());
    this.channel.on("error", (error) => this._handleError(error));
    this.channel.on("reconnect", () => this._handleReconnect());
    this.channel.on("message", (topic, message) =>
      this.handleMessage({ topic, message }),
    );
  }

  reconnect(password) {
    console.dev(`MQTT: reconnect`);
    this.password = password;
    if (this.channel) {
      this.channel.end(false, () => {
        this.channel = null;
        this.forceReconnect = true;
        this.connect(this.clientID, this.username, this.password);
      });
    }
  }

  end(callback) {
    this.channel.end(false, () => {
      this.channel = null;
      this.subscriptions = [];
      callback();
    });
  }

  publish(topic, data) {
    console.dev(`publishing to ${topic}`);
    this.channel.publish(topic, data);
  }

  publishRequestFrequentLogs(deviceMACAddress, clientUUID) {
    let logRequest = new log_pb.LogRequest();
    logRequest.setDeviceId(clientUUID);
    logRequest.setStartOrStop(true); // true = start sending logs, false = stop sending logs
    // logRequest.setDumpHistory(true);
    logRequest.setExtendedLog(false);
    const bytes = logRequest.serializeBinary();

    this.publish(mqttTopics.logRequestTopic + deviceMACAddress, bytes);
  }

  publishStopFrequentLogs(deviceMACAddress, clientUUID) {
    let logRequest = new log_pb.LogRequest();
    logRequest.setDeviceId(clientUUID);
    logRequest.setStartOrStop(false); // true = start sending logs, false = stop sending logs
    // logRequest.setDumpHistory(true);
    logRequest.setExtendedLog(false);
    const bytes = logRequest.serializeBinary();

    this.publish(mqttTopics.logRequestTopic + deviceMACAddress, bytes);
  }

  subscribe(topic) {
    if (this.subscriptions.includes(topic)) {
      console.dev("already subscribed to topic");
      return;
    }
    if (this.isTopicRefused(topic)) {
      console.dev(`subscribe to ${topic} suppressed: refused, in backoff`);
      return;
    }
    console.dev(`subscribing to ${topic}`);
    this.subscriptions.push(topic);
    this.pendingSubscribes.add(topic);
    this.channel.subscribe(topic, (err, granted) =>
      this._handleSuback(topic, err, granted),
    );
  }

  _handleSuback(topic, err, granted) {
    this.pendingSubscribes.delete(topic);
    const grant = Array.isArray(granted)
      ? granted.find((g) => g.topic === topic) || granted[0]
      : null;
    const refused = Boolean(err) || (grant && grant.qos === REFUSED_QOS);
    if (refused) {
      this._markRefused(topic);
    } else {
      this.refusedTopics.delete(topic);
    }
  }

  _markRefused(topic) {
    const previous = this.refusedTopics.get(topic);
    const count = previous ? previous.count + 1 : 1;
    const delay = Math.min(
      REFUSED_BACKOFF_BASE_MS * 2 ** (count - 1),
      REFUSED_BACKOFF_MAX_MS,
    );
    this.refusedTopics.set(topic, { count, retryAfter: Date.now() + delay });
    const index = this.subscriptions.indexOf(topic);
    if (index !== -1) this.subscriptions.splice(index, 1);
    console.dev(
      `subscribe to ${topic} refused (#${count}); retry in ${delay}ms`,
    );
  }

  isTopicRefused(topic) {
    const entry = this.refusedTopics.get(topic);
    return Boolean(entry) && Date.now() < entry.retryAfter;
  }

  _resetRefusals() {
    this.refusedTopics.clear();
    this.pendingSubscribes.clear();
  }

  subscribeToNotifications(UserUuid) {
    this.subscribe(mqttTopics.notificationTopic + UserUuid);
  }

  subscribeFrequentLog(deviceUuid) {
    this.subscribe(mqttTopics.logsTopic + deviceUuid);
  }

  unsubscribeFrequentLog(deviceUuid) {
    this.unsubscribe(mqttTopics.logsTopic + deviceUuid);
  }

  unsubscribeAllFrequentLog() {
    this.subscriptions.forEach((subscription) => {
      if (subscription.indexOf(mqttTopics.logsTopic) !== -1) {
        this.unsubscribe(subscription);
      }
    });
  }

  subscribeFrequentLogResp(deviceUuid) {
    this.subscribe(mqttTopics.logRequestResponseTopic + deviceUuid);
  }

  unsubscribeFrequentLogResp(deviceUuid) {
    this.unsubscribe(mqttTopics.logRequestResponseTopic + deviceUuid);
  }

  unsubscribe(topic) {
    if (this.subscriptions.includes(topic)) {
      console.dev(`subscribing to ${topic}`);

      this.channel.unsubscribe(topic);
      this.subscriptions.splice(this.subscriptions.indexOf(topic), 1);
      this.pendingSubscribes.delete(topic);
    } else {
      console.dev("can't unsubscribe because no subscription is present");
    }
  }

  handleMessage(action) {
    const isNotification =
      action.topic.indexOf("backend/notifications/") !== -1;
    let isLog = action.topic.indexOf(mqttTopics.logsTopic) !== -1;
    let isLogRequestResponse =
      action.topic.indexOf(mqttTopics.logRequestResponseTopic) !== -1;

    // If the received message is a notification, send a response that we received the notification
    if (isNotification) {
      const userUUID = action.topic.substr(action.topic.lastIndexOf("/") + 1);

      // Deserialize notification
      let message;
      try {
        message = notification_pb.Notification.deserializeBinary(
          new Uint8Array(action.message),
        ).toObject();
      } catch (err) {
        console.log("NOTIFICATION ERROR");
      }

      // Create notification response
      let notResponse = new notification_pb.NotificationResp();
      notResponse.setSeq(message.seq);
      const bytes = notResponse.serializeBinary();

      // Perhaps dispatch a new action instead of calling channel here
      this.channel.publish(
        mqttTopics.notificationResponseTopic + userUUID,
        bytes,
      );

      // Inform observers that a notification was received
      this.notification$.next(message);
    } else if (isLog) {
      const message = log_pb.Logs.deserializeBinary(
        new Uint8Array(action.message),
      ).toObject();
      this.frequentLog$.next(message);
    } else if (isLogRequestResponse) {
      let message;
      try {
        message = log_pb.LogRequestResp.deserializeBinary(
          new Uint8Array(action.message),
        ).toObject();
      } catch (err) {
        console.log("LOG REQUEST RESPONSE ERROR");
      }

      this.frequentLogResponse$.next(message);
    }
  }

  exit() {}

  _generateUUID = () => {
    let d = new Date().getTime();
    let uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        let r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
      },
    );
    return uuid;
  };

  _handleConnect() {
    this.connected = true;
    this.connected$.next(true);

    // Replay subscriptions on every (re)connect: mqtt.js' own resubscribe is
    // disabled (see connect options) so that refused topics stay held back.
    this.forceReconnect = false;
    const subscriptionsCopy = [...this.subscriptions];
    this.subscriptions = [];
    subscriptionsCopy.forEach((subscription) => {
      this.subscribe(subscription);
    });
  }
  _handleReconnect() {
    console.dev("reconnecting");
  }
  _handleClose() {
    this.connected = false;
    this.connected$.next(false);
    console.dev("disconnect");
    // A close while a SUBSCRIBE is still un-SUBACKed means the broker killed
    // the connection over that subscribe (RabbitMQ 3.6 access_refused path).
    // Treat those topics as refused so the reconnect doesn't replay them.
    [...this.pendingSubscribes].forEach((topic) => this._markRefused(topic));
    this.pendingSubscribes.clear();
  }
  _handleError(error) {
    this.connected = false;
    this.connected$.next(false);
    console.error(error);
  }

  isConnected() {
    return this.channel && this.channel.connected;
  }
}

const mqttService = new MqttService();
export default mqttService;
